import { Descendant, Editor, Node, Path, Range, Transforms } from 'slate'

import {
  BLOCK_FORMATS,
  TCustomElement,
} from 'core-components/library/richTextEditor/interface/custom-types'

import { CustomEditor } from '../interface/custom-types'

import { changeTextIndent } from './copyFromOtherEditorHelpers/changeTextIndentHelper'
import { prepareImagesFromOtherEditors } from './copyFromOtherEditorHelpers/prepareImagesFromOtherEditors'
import { prepareListsFromOtherEditors } from './copyFromOtherEditorHelpers/prepareListsFromOtherEditors'
import { prepareTablesFromOtherEditors } from './copyFromOtherEditorHelpers/prepareTablesFromOtherEditors'
import { removeExtraParagraph } from './copyFromOtherEditorHelpers/removeExtraParagraphHelper'
import { removeExtraSpans } from './copyFromOtherEditorHelpers/removeExtraSpansHelper'
import { unwrapFromBElement } from './copyFromOtherEditorHelpers/unwrapFromBElement'
import { isValidSelector } from './copyFromOtherEditorHelpers/validationSelectorHelper'
import { wrapListsInParagraph } from './copyFromOtherEditorHelpers/wrapListsInParagraph'
import { parseHtmlToValue } from './htmlHelper'

export const insertText = (editor: CustomEditor, nodesFromCopyElement: Descendant[]) => {
  if (editor.selection) {
    const preparedLists = nodesFromCopyElement.map((node) => {
      if (
        node['type'] === BLOCK_FORMATS.numberedList ||
        node['type'] === BLOCK_FORMATS.bulletedList
      ) {
        const newList = (node as TCustomElement).children.filter((item) => !item['text'])
        return { ...node, children: newList }
      }
      return node
    })

    const currentNode = Node.get(editor, editor.selection.anchor.path)
    const isEmptyLine = Node.string(currentNode).length === 0
    const isTextNodes = preparedLists.every((node) => node['text'])
    if (isEmptyLine && !isTextNodes) {
      Transforms.insertNodes(editor, preparedLists, {
        at: Path.parent(editor.selection.anchor.path),
        select: true,
      })
    } else {
      Transforms.insertNodes(editor, preparedLists, {
        at: editor.selection,
        select: true,
      })
    }
    Transforms.collapse(editor, { edge: 'end' })
  }
}

export const insertDataWithHtmlParsing = (editor: CustomEditor, data: string) => {
  const parsedDoc = new DOMParser().parseFromString(data, 'text/html')

  // sometimes instead of marginLeft in styles there is textIndent, which is not
  // supported by the editor, so we replace them with marginLeft
  changeTextIndent(parsedDoc)

  // when copying from some editors, the content comes wrapped in the b tag,
  // we delete it so as not to save unnecessary wrappers
  const boldElements = parsedDoc.querySelectorAll('body > b')
  boldElements.forEach((boldElement) => {
    unwrapFromBElement(boldElement)
  })

  prepareListsFromOtherEditors(parsedDoc)

  // remove p if img wraped in p
  prepareImagesFromOtherEditors(parsedDoc)

  prepareTablesFromOtherEditors(parsedDoc)

  // Remove the paragraph that wraps the text in order to insert it inside the existing text and not into a new node
  if (editor.selection) {
    const currentNode = Node.get(editor, editor.selection.anchor.path)
    const isEmptyLine = Node.string(currentNode).length === 0
    if (!isEmptyLine) {
      removeExtraParagraph(parsedDoc)
    }
  }

  //we insert text from an unknown source, in order to preserve the formatting,
  //we use the transformation without minification,
  // because it removes the necessary spaces and add the result to the editor
  const nodesFromCopyElement = parseHtmlToValue({
    value: parsedDoc.documentElement.innerHTML,
    withoutMinify: true,
  })
  if (nodesFromCopyElement && editor.selection) {
    //check if the text is selected, then delete it before pasting the copied text
    if (!Range.isCollapsed(editor.selection)) {
      Editor.deleteFragment(editor)
    }
    insertText(editor, nodesFromCopyElement)
  }
}

export const insertDataFromOtherEditors = (editor: CustomEditor, data: string) => {
  const parsedDoc = new DOMParser().parseFromString(data, 'text/html')

  const styleTags = parsedDoc.querySelectorAll('style')
  styleTags.forEach((styleTag) => {
    const cssText = styleTag.textContent
    if (cssText) {
      const rules = cssText.split('}')
      rules.forEach((rule) => {
        const trimmedRule = rule.trim()
        if (trimmedRule) {
          const [selector, style] = trimmedRule.split('{')
          if (isValidSelector(selector)) {
            const elements = parsedDoc.querySelectorAll(selector.trim())
            elements.forEach((element) => {
              const newStyles = element.getAttribute('style')
                ? `${style}; ${element.getAttribute('style')}`
                : style
              element.setAttribute('style', `${newStyles}`)
            })
          }
        }
      })
    }
  })

  // Remove spans that don`t contain styles for parsing
  removeExtraSpans(parsedDoc)

  const preparedForParseValue = wrapListsInParagraph(parsedDoc.body.innerHTML.replace(/\n/g, ' '))
  insertDataWithHtmlParsing(editor, preparedForParseValue)
}
